import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';


const SecuritySection = () => {
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(true);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const activeSessions = [
    {
      id: 1,
      device: "MacBook Pro",
      browser: "Chrome 120.0",
      location: "New York, NY",
      lastActive: "2 minutes ago",
      current: true
    },
    {
      id: 2,
      device: "iPhone 15",
      browser: "Safari Mobile",
      location: "New York, NY",
      lastActive: "1 hour ago",
      current: false
    },
    {
      id: 3,
      device: "Windows PC",
      browser: "Edge 119.0",
      location: "Boston, MA",
      lastActive: "3 days ago",
      current: false
    }
  ];

  const loginHistory = [
    {
      id: 1,
      timestamp: "2025-01-11 08:12:33",
      device: "MacBook Pro",
      location: "New York, NY",
      status: "success"
    },
    {
      id: 2,
      timestamp: "2025-01-10 14:30:15",
      device: "iPhone 15",
      location: "New York, NY",
      status: "success"
    },
    {
      id: 3,
      timestamp: "2025-01-09 09:45:22",
      device: "Windows PC",
      location: "Boston, MA",
      status: "failed"
    },
    {
      id: 4,
      timestamp: "2025-01-08 16:20:10",
      device: "MacBook Pro",
      location: "New York, NY",
      status: "success"
    }
  ];

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validatePasswordForm = () => {
    const newErrors = {};
    
    if (!passwordData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    
    if (!passwordData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }
    
    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordSubmit = async () => {
    if (!validatePasswordForm()) return;
    
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsLoading(false);
    setIsChangingPassword(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    
    console.log('Password changed successfully');
  };

  const handleToggle2FA = async () => {
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setTwoFactorEnabled(!twoFactorEnabled);
    setIsLoading(false);
    
    console.log(`2FA ${!twoFactorEnabled ? 'enabled' : 'disabled'}`);
  };

  const handleTerminateSession = async (sessionId) => {
    console.log(`Terminating session ${sessionId}`);
    // Implement session termination logic
  };

  const getStatusIcon = (status) => {
    return status === 'success' ? 
      <Icon name="CheckCircle" size={16} className="text-success" /> :
      <Icon name="XCircle" size={16} className="text-destructive" />;
  };

  return (
    <div className="space-y-6">
      {/* Password Management */}
      <div className="card-elevated p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Lock" size={20} className="text-warning" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Password & Security</h2>
              <p className="text-sm text-muted-foreground">Manage your account security settings</p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Change Password */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-medium text-foreground">Password</h3>
                <p className="text-xs text-muted-foreground">Last changed 30 days ago</p>
              </div>
              {!isChangingPassword && (
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Key"
                  iconPosition="left"
                  onClick={() => setIsChangingPassword(true)}
                >
                  Change Password
                </Button>
              )}
            </div>

            {isChangingPassword && (
              <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                <Input
                  label="Current Password"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                  error={errors.currentPassword}
                  required
                />
                
                <Input
                  label="New Password"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                  error={errors.newPassword}
                  description="Must be at least 8 characters long"
                  required
                />
                
                <Input
                  label="Confirm New Password"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                  error={errors.confirmPassword}
                  required
                />
                
                <div className="flex items-center justify-end space-x-3 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsChangingPassword(false);
                      setPasswordData({
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: ''
                      });
                      setErrors({});
                    }}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handlePasswordSubmit}
                    loading={isLoading}
                    iconName="Save"
                    iconPosition="left"
                  >
                    Update Password
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Two-Factor Authentication */}
          <div className="border-t border-border pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-foreground">Two-Factor Authentication</h3>
                <p className="text-xs text-muted-foreground">
                  Add an extra layer of security to your account
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`text-xs px-2 py-1 rounded-full ${
                  twoFactorEnabled 
                    ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                }`}>
                  {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                </span>
                <Button
                  variant={twoFactorEnabled ? "destructive" : "default"}
                  size="sm"
                  onClick={handleToggle2FA}
                  loading={isLoading}
                  iconName={twoFactorEnabled ? "Shield" : "ShieldCheck"}
                  iconPosition="left"
                >
                  {twoFactorEnabled ? 'Disable' : 'Enable'} 2FA
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active Sessions */}
      <div className="card-elevated p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="Monitor" size={20} className="text-secondary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Active Sessions</h2>
            <p className="text-sm text-muted-foreground">Manage devices that are currently signed in</p>
          </div>
        </div>

        <div className="space-y-4">
          {activeSessions.map((session) => (
            <div key={session.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-background rounded-lg flex items-center justify-center">
                  <Icon name={session.device.includes('iPhone') ? 'Smartphone' : 'Monitor'} size={20} />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h4 className="text-sm font-medium text-foreground">{session.device}</h4>
                    {session.current && (
                      <span className="text-xs bg-success/10 text-success px-2 py-1 rounded-full">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{session.browser}</p>
                  <p className="text-xs text-muted-foreground">{session.location} • {session.lastActive}</p>
                </div>
              </div>
              
              {!session.current && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTerminateSession(session.id)}
                  iconName="LogOut"
                  iconPosition="left"
                >
                  Terminate
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Login History */}
      <div className="card-elevated p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="History" size={20} className="text-accent" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Login History</h2>
            <p className="text-sm text-muted-foreground">Recent account access attempts</p>
          </div>
        </div>

        <div className="space-y-3">
          {loginHistory.map((login) => (
            <div key={login.id} className="flex items-center justify-between p-3 hover:bg-muted/30 rounded-lg transition-colors">
              <div className="flex items-center space-x-3">
                {getStatusIcon(login.status)}
                <div>
                  <p className="text-sm font-medium text-foreground">{login.device}</p>
                  <p className="text-xs text-muted-foreground">{login.location}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">{login.timestamp}</p>
                <p className={`text-xs font-medium ${
                  login.status === 'success' ? 'text-success' : 'text-destructive'
                }`}>
                  {login.status === 'success' ? 'Successful' : 'Failed'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SecuritySection;